import React from 'react';
import { API_CONFIG } from '../../../config/api';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    TextInput,
    DateInput,
    Filter,
    TopToolbar,
    ExportButton,
    RefreshButton,
    useTranslate,
    FunctionField,
    usePermissions,
    SelectInput,
    useRecordContext,
    ShowButton,
    useRefresh,
    useNotify,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, Stack, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Send as SendingIcon,
    Refresh as RetryIcon,
} from '@mui/icons-material';

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon sx={{ fontSize: 14 }} />,
    sms: <SmsIcon sx={{ fontSize: 14 }} />,
    whatsapp: <WhatsAppIcon sx={{ fontSize: 14 }} />,
    push: <PushIcon sx={{ fontSize: 14 }} />,
    slack: <SlackIcon sx={{ fontSize: 14 }} />,
};

const statusIcons: Record<string, React.ReactElement> = {
    queued: <PendingIcon sx={{ fontSize: 14 }} />,
    sending: <SendingIcon sx={{ fontSize: 14 }} />,
    sent: <SuccessIcon sx={{ fontSize: 14 }} />,
    delivered: <SuccessIcon sx={{ fontSize: 14 }} />,
    failed: <ErrorIcon sx={{ fontSize: 14 }} />,
    bounced: <ErrorIcon sx={{ fontSize: 14 }} />,
};

const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
        queued: '#FF9800',
        sending: '#2196F3',
        sent: '#4CAF50',
        delivered: '#4CAF50',
        failed: '#F44336',
        bounced: '#F44336',
    };
    return colors[status] || '#757575';
};

const getChannelColor = (channel: string): string => {
    const colors: Record<string, string> = {
        email: '#1976D2',
        sms: '#4CAF50',
        whatsapp: '#25D366',
        push: '#FF6B35',
        slack: '#4A154B',
    };
    return colors[channel] || '#757575';
};

// Filter component for notification logs
const NotificationLogFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <SelectInput
                label="Log source"
                source="source"
                alwaysOn
                choices={[
                    { id: 'expo_push', name: 'Charging push notifications' },
                    { id: 'message_recipients', name: 'Broadcast message deliveries' },
                    { id: 'notification_log', name: 'System notifications' },
                ]}
            />
            <TextInput
                label={translate('resources.notification-logs.list.filters.search')}
                source="q"
                alwaysOn
            />
            <SelectInput
                label={translate('resources.notification-logs.list.filters.status')}
                source="status"
                choices={[
                    { id: 'queued', name: translate('resources.notification-logs.status.queued') },
                    { id: 'sending', name: translate('resources.notification-logs.status.sending') },
                    { id: 'sent', name: translate('resources.notification-logs.status.sent') },
                    { id: 'delivered', name: translate('resources.notification-logs.status.delivered') },
                    { id: 'failed', name: translate('resources.notification-logs.status.failed') },
                    { id: 'bounced', name: translate('resources.notification-logs.status.bounced') },
                ]}
            />
            <SelectInput
                label={translate('resources.notification-logs.list.filters.channel')}
                source="channel"
                choices={[
                    { id: 'email', name: translate('resources.notification-logs.channels.email') },
                    { id: 'sms', name: translate('resources.notification-logs.channels.sms') },
                    { id: 'whatsapp', name: translate('resources.notification-logs.channels.whatsapp') },
                    { id: 'push', name: translate('resources.notification-logs.channels.push') },
                    { id: 'slack', name: translate('resources.notification-logs.channels.slack') },
                ]}
            />
            <SelectInput
                label={translate('resources.notification-logs.list.filters.recipient_type')}
                source="recipient_type"
                choices={[
                    { id: 'app_user', name: translate('resources.notification-logs.recipient_types.app_user') },
                    { id: 'backend_user', name: translate('resources.notification-logs.recipient_types.backend_user') },
                    { id: 'external', name: translate('resources.notification-logs.recipient_types.external') },
                ]}
            />
            <DateInput
                label={translate('resources.notification-logs.list.filters.startDate')}
                source="startDate"
            />
            <DateInput
                label={translate('resources.notification-logs.list.filters.endDate')}
                source="endDate"
            />
        </Filter>
    );
};

const RetryButton = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const { permissions } = usePermissions();
    
    if (!record || record.status !== 'failed' || record._source !== 'notification_log' || !permissions?.includes('notifications:write:all')) {
        return null;
    }
    
    const handleRetry = async () => {
        try {
            const response = await fetch(`${API_CONFIG.baseURL}/api/backend/notification-logs/${record.id}/retry`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            
            if (response.ok) {
                notify('Notification queued for retry', { type: 'success' });
                refresh();
            } else {
                notify('Failed to retry notification', { type: 'error' });
            }
        } catch (error) {
            notify('Failed to retry notification', { type: 'error' });
        }
    };
    
    return (
        <Tooltip title="Retry">
            <IconButton size="small" onClick={handleRetry} color="primary">
                <RetryIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <RefreshButton />
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/notification-logs/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                icon={statusIcons[record.status]}
                                label={translate(`resources.notification-logs.status.${record.status}`)}
                                color={(record.status === 'sent' || record.status === 'delivered' ? 'success' :
                                    record.status === 'failed' || record.status === 'bounced' ? 'error' :
                                    record.status === 'sending' ? 'info' : 'warning') as any}
                                size="small"
                                sx={{ fontWeight: 500 }}
                            />
                            <Chip
                                icon={channelIcons[record.channel]}
                                label={translate(`resources.notification-logs.channels.${record.channel}`)}
                                size="small"
                                sx={{
                                    fontWeight: 500,
                                    backgroundColor: getChannelColor(record.channel) + '20',
                                    color: getChannelColor(record.channel),
                                    '& .MuiChip-icon': { color: getChannelColor(record.channel) },
                                }}
                            />
                            {record.retry_count > 0 && (
                                <Chip
                                    label={translate('resources.notification-logs.details.retries', { count: record.retry_count })}
                                    size="small"
                                    color="warning"
                                />
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {record.recipientName || record.recipient_id}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {record.created_at && new Date(record.created_at).toLocaleString()}
                            </Typography>
                            {record.error_message && (
                                <Typography variant="caption" color="error" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                                    {record.error_message}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const LogList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const canView = permissions?.includes('notifications:read:all');

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>
                    {translate('resources.notification-logs.permissions.no_access')}
                </Typography>
            </Box>
        );
    }

    return (
        <List
            filters={<NotificationLogFilter />}
            filterDefaultValues={{ source: 'expo_push' }}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            empty={isMobile ? false : undefined}
            title={translate('resources.notification-logs.list.title')}
            perPage={25}
            sort={{ field: 'created_at', order: 'DESC' }}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile && {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                    '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                    '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                    '& .RaListToolbar-root > span': { display: 'none' },
                    '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                    '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                    '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                    '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                    '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                    '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                }),
            }}
        >
            {isMobile ? (
                <MobileList />
            ) : (
                <Datagrid
                    bulkActionButtons={false}
                    rowClick="show"
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                                cursor: 'pointer',
                            },
                        },
                    }}
                >
                    <FunctionField
                        label={translate('resources.notification-logs.fields.status')}
                        render={(record: any) => {
                            const statusColor = record.status === 'sent' || record.status === 'delivered' ? 'success' :
                                              record.status === 'failed' || record.status === 'bounced' ? 'error' :
                                              record.status === 'sending' ? 'info' : 'warning';
                            return (
                                <Chip
                                    icon={statusIcons[record.status]}
                                    label={translate(`resources.notification-logs.status.${record.status}`)}
                                    color={statusColor as any}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                />
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.notification-logs.fields.channel')}
                        render={(record: any) => (
                            <Chip
                                icon={channelIcons[record.channel]}
                                label={translate(`resources.notification-logs.channels.${record.channel}`)}
                                size="small"
                                sx={{
                                    fontWeight: 500,
                                    backgroundColor: getChannelColor(record.channel) + '20',
                                    color: getChannelColor(record.channel),
                                    '& .MuiChip-icon': {
                                        color: getChannelColor(record.channel),
                                    },
                                }}
                            />
                        )}
                    />

                    <FunctionField
                        label={translate('resources.notification-logs.fields.notification')}
                        render={(record: any) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.type}
                                </Typography>
                                {record.template && (
                                    <Typography variant="caption" color="text.secondary">
                                        {record.template}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.notification-logs.fields.recipient')}
                        render={(record: any) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.recipientName || record.recipient_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {translate(`resources.notification-logs.recipient_types.${record.recipient_type}`)}
                                </Typography>
                            </Box>
                        )}
                    />

                    <DateField
                        source="created_at"
                        label="resources.notification-logs.fields.created_at"
                        showTime
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                        }}
                    />

                    <FunctionField
                        label={translate('resources.notification-logs.fields.details')}
                        render={(record: any) => {
                            if (record.retry_count > 0) {
                                return (
                                    <Chip
                                        label={translate('resources.notification-logs.details.retries', { count: record.retry_count })}
                                        size="small"
                                        color="warning"
                                    />
                                );
                            }
                            if (record.error_message) {
                                return (
                                    <Typography variant="caption" color="error" sx={{
                                        display: 'inline-block',
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {record.error_message}
                                    </Typography>
                                );
                            }
                            return (
                                <Typography variant="caption" color="text.secondary">
                                    {translate('resources.notification-logs.details.noDetails')}
                                </Typography>
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.notification-logs.fields.actions')}
                        render={() => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <RetryButton />
                                <ShowButton />
                            </Box>
                        )}
                    />
                </Datagrid>
            )}
        </List>
    );
};